import { useContext, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, MoreVertical, Pause, Play } from 'lucide-react';
import './TrackList.css';
import { PlayerContext } from '../../../context/PlayerContext';
import { Track } from './track';
import { TrackMenu } from '../trackMenu/TrackMenu';
import { TrackInfoModal } from '../trackMenu/TrackInfoModal';
import { useTrackMenu } from '../../../hooks/useTrackMenu';
import { coverArtUrl } from '../../../config/images';
import { AlbumArt } from '../cards/AlbumArt';

interface Props {
    query?: string;
    // items overrides the context track list: the Songs page fetches a
    // server-filtered, server-sorted page and wants exactly those rows in
    // exactly that order, with no client-side re-filtering.
    items?: Track[];
    // loading lets an owner render the loading state while its own fetch
    // is in flight (the context's tracksLoading only covers the shared list).
    loading?: boolean;
}

function TrackList({ query, items, loading }: Props): JSX.Element {
    const navigate = useNavigate();
    const { tracks: contextTracks, tracksLoading, currentSong, isPlaying, playTrackNow, togglePlay } =
        useContext(PlayerContext);
    const { menu, openAtCursor, openAtElement, closeMenu, infoTrack, openTrackInfo, closeTrackInfo } = useTrackMenu();

    const tracks = items ?? contextTracks;

    const handleClick = (track: Track): void => {
        if (currentSong?.getId() === track.getId()) togglePlay();
        else playTrackNow(track);
    };

    // A server-provided list is already filtered and ordered; only the
    // context list gets the local query filter.
    const normalizedQuery = items ? '' : query?.trim().toLowerCase() ?? '';
    const visibleTracks = normalizedQuery
        ? tracks.filter(
              (track) =>
                  track.getTitle().toLowerCase().includes(normalizedQuery) ||
                  track.getArtists().some((a) => a.title.toLowerCase().includes(normalizedQuery))
          )
        : tracks;

    if (loading ?? (items ? false : tracksLoading)) {
        return <div className="trackListEmpty">Loading your library…</div>;
    }

    if (tracks.length === 0) {
        return <div className="trackListEmpty">{items ? 'No songs match the current filters' : 'No files in library'}</div>;
    }

    if (visibleTracks.length === 0) {
        return <div className="trackListEmpty">No tracks match your search</div>;
    }

    return (
        <div className="trackGrid">
            {visibleTracks.map((track) => {
                const isThisTrackPlaying = currentSong?.getId() === track.getId() && isPlaying;
                const art = coverArtUrl(track.getAlbumId());
                return (
                    <div
                        key={track.getId()}
                        className={`trackCard ${
                            currentSong?.getId() === track.getId() ? 'trackCardActive' : ''
                        }`}
                        onClick={() => handleClick(track)}
                        onContextMenu={(e) => openAtCursor(e, track)}
                    >
                        <div className="trackArt">
                            {art ? (
                                <AlbumArt className="trackArtImage" img={art} title={track.getTitle()} iconSize={22} />
                            ) : (
                                <Music className="trackArtIcon" size={22} strokeWidth={2.75} />
                            )}
                            {isThisTrackPlaying ? (
                                <Pause className="trackArtPlayIcon" size={22} strokeWidth={2.75} fill="currentColor" />
                            ) : (
                                <Play className="trackArtPlayIcon" size={22} strokeWidth={2.75} fill="currentColor" />
                            )}
                        </div>
                        <div className="trackMeta">
                            <span className="trackTitle">{track.getTitle()}</span>
                            <span className="trackArtist">
                                {track.getArtists().map((a, i) => (
                                    <span key={a.id}>
                                        {i > 0 && ', '}
                                        <span
                                            className="trackArtistLink"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/artists/${a.id}`);
                                            }}
                                        >
                                            {a.title}
                                        </span>
                                    </span>
                                ))}
                            </span>
                        </div>
                        <button
                            className="trackCardMenuButton"
                            onClick={(e) => openAtElement(e, track)}
                            aria-label={`More options for ${track.getTitle()}`}
                        >
                            <MoreVertical size={16} strokeWidth={2.75} />
                        </button>
                    </div>
                );
            })}
            {menu && (
                <TrackMenu track={menu.track} x={menu.x} y={menu.y} onClose={closeMenu} onShowInfo={openTrackInfo} />
            )}
            {infoTrack && <TrackInfoModal track={infoTrack} onClose={closeTrackInfo} />}
        </div>
    );
}

export default TrackList;
